import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { getDailyRestNeeded, getRemainingDailyRestRequired, getTotalDailyRestRequired } from "../availabilityHelpers";
import { getBeginningOfDay, getBeginningOfNextDay, getDateIndex, getPeriodOverlap, getTimeLeftInDay, offDutyStatuses, type IState, type IWorkday } from "../helpers";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type DailyRestConditions = {
    avail: number | undefined;
    restNeeded: number | undefined;
    requirementMet: boolean;
    isOffDuty: boolean;
    uncredited: boolean;
    hasAnchor: boolean;
    hasCurrentLog: boolean;
    stateKind: "RequirementMetOnDuty" | "RequirementMetOffDuty" | "OnDuty" | "OffDutyUncreditedNoLog" | "OffDutyUncreditedAtBoundary" | "OffDutyUncredited" | "OffDutyCredited";
    /**
     * The day's requirement is zeroed by an AdverseWeather log in the last log segment.
     * applyAdverseLog runs after allocateCounts has moved to the segment end's day, so such a log
     * lands on the evaluation's own day whatever day that is: every midnight keeps it zeroed.
     */
    zeroedRequirementFollowsEvalDay?: boolean;
};

export class DailyRestCalculator extends AvailabilityCalculator {
    private conditions(state: IState): DailyRestConditions {
        const avail = discreteAvailabilityFunctions.DailyRest(state, false);
        const restNeeded = getDailyRestNeeded(state);

        const isOffDuty = offDutyStatuses.indexOf(state.current?.status) !== -1;
        const restBreakMinimum = state.config?.rest?.restBreakMinimum ?? 0;
        const workdayConsecutiveOff = state.workday.consecutiveOffCount ?? 0;
        const isWtResettingOffCount = state.current?.status === "WT" &&
            state.config.workday?.consecutiveOffCountIncludesWt === false;
        const effectiveConsecutiveOff = isWtResettingOffCount
            ? (state.workday.consecutiveWtCount ?? 0)
            : workdayConsecutiveOff;
        const consecutiveOffStartTime = state.workday.consecutiveOffStartTime;

        const hasTempCreditedBreak = state.day.restInfo?.some(ri => ri.isTemp) ?? false;
        const isCreditedViaPastAnchor = consecutiveOffStartTime !== undefined &&
            consecutiveOffStartTime.getTime() + restBreakMinimum <= state.nextDate.getTime();

        const requirementMet = (restNeeded ?? 0) === 0;
        const uncredited = restBreakMinimum > 0 && workdayConsecutiveOff < restBreakMinimum && !hasTempCreditedBreak && !isCreditedViaPastAnchor;
        const hasAnchor = !(consecutiveOffStartTime === undefined && effectiveConsecutiveOff === 0);
        const hasCurrentLog = state.current?.dateTime !== undefined;

        let stateKind: DailyRestConditions["stateKind"];
        if (requirementMet && !isOffDuty) stateKind = "RequirementMetOnDuty";
        else if (requirementMet) stateKind = "RequirementMetOffDuty";
        else if (!isOffDuty) stateKind = "OnDuty";
        else if (!uncredited) stateKind = "OffDutyCredited";
        else if (hasAnchor) stateKind = "OffDutyUncredited";
        else if (!hasCurrentLog) stateKind = "OffDutyUncreditedNoLog";
        else stateKind = "OffDutyUncreditedAtBoundary";

        let zeroedRequirementFollowsEvalDay: boolean | undefined = undefined;
        if (stateKind === "RequirementMetOnDuty") {
            // After the last step state.current is the final log itself; the segment that step
            // processed opens at the log before it.
            const adverseLogStart = state.workday.adverseLogStart;
            const lastSegmentStart = state.logs.length >= 2 ? state.logs[state.logs.length - 2].dateTime : undefined;
            zeroedRequirementFollowsEvalDay = getTotalDailyRestRequired(state.day, state.config) === 0
                && !!state.day.isAdverseLogApplied
                && adverseLogStart !== undefined
                && lastSegmentStart !== undefined
                && adverseLogStart.getTime() >= new Date(lastSegmentStart).getTime();
        }

        return {
            avail,
            restNeeded,
            requirementMet,
            isOffDuty,
            uncredited,
            hasAnchor,
            hasCurrentLog,
            stateKind,
            zeroedRequirementFollowsEvalDay
        };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        const c = this.conditions(state);
        return c.stateKind !== "OffDutyCredited";
    }

    exhaustion(state: IState): Date | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        if (c.stateKind === "OnDuty" || c.stateKind === "OffDutyUncreditedAtBoundary" || c.stateKind === "OffDutyUncredited") {
            const timeLeftInDay = getTimeLeftInDay(state);
            if (c.avail > 0 && c.avail < timeLeftInDay) {
                return new Date(state.nextDate.getTime() + c.avail);
            }
        }

        // RequirementMetOnDuty: avail = look-ahead formula spanning two days, so avail >
        // timeLeftInDay and the guard above skips it. The zero-crossing IS real — it's the point
        // of no return for tomorrow's requirement — unless the zeroed requirement follows the
        // evaluation day, when midnight always comes first. Route it through exhaustion() so the
        // stability checker can verify it automatically via nextExhaustionEvent.
        if (c.stateKind === "RequirementMetOnDuty" && !c.zeroedRequirementFollowsEvalDay && c.avail > 0) {
            return new Date(state.nextDate.getTime() + c.avail);
        }

        return undefined;
    }

    private creditEvent(state: IState): Date {
        const restBreakMinimum = state.config?.rest?.restBreakMinimum ?? 0;
        const workdayConsecutiveOff = state.workday.consecutiveOffCount ?? 0;
        const isWtResettingOffCount = state.current?.status === "WT" &&
            state.config.workday?.consecutiveOffCountIncludesWt === false;
        const effectiveConsecutiveOff = isWtResettingOffCount
            ? (state.workday.consecutiveWtCount ?? 0)
            : workdayConsecutiveOff;
        const consecutiveOffStartTime = state.workday.consecutiveOffStartTime?.getTime();
        const breakStartTime = consecutiveOffStartTime ?? (state.nextDate.getTime() - effectiveConsecutiveOff);
        return new Date(breakStartTime + restBreakMinimum);
    }

    /**
     * Before midnight, when a workday reset takes back the daily rest this break has supplied, and
     * when the regrowing allocation meets the requirement again. Until the reset, countOffDutyTime
     * passes the whole break to countDailyRestBreaks; from the reset on,
     * getAdditionalConsecutiveOffDutyTime subtracts the reset's offDutyMinimum, so the allocation
     * falls to zero and regrows at +1. That matters only if today's rest relied on the allocation.
     * A WT break is allocated from consecutiveWtCount, which the reset does not touch.
     */
    private workdayResetDrop(state: IState, nextDayBoundary: Date): { resetDate: Date; requirementMetDate: Date | undefined } | undefined {
        const totalRest = getTotalDailyRestRequired(state.day, state.config);
        if (!state.workday.hasStarted || state.current?.status === "WT" || !state.day.restInfo || totalRest === undefined) {
            return undefined;
        }
        const allocated = state.day.restInfo
            .filter(r => r.isTemp && !r.isWt)
            .reduce((sum, r) => sum + (r.toDate.getTime() - r.fromDate.getTime()), 0);
        const restBeforeBreak = (state.day.restCount ?? 0) - allocated;
        if (allocated === 0 || restBeforeBreak >= totalRest) {
            return undefined;
        }
        const resetDate = this.workdayResetDate(state);
        const breakStart = state.workday.consecutiveOffStartTime;
        if (!resetDate || !breakStart || resetDate.getTime() >= nextDayBoundary.getTime()) {
            return undefined;
        }
        // checkIfWorkdayReset runs the MYG-16078 credit in the reset's own tick, before
        // startNewWorkday, and again on every pass while the new workday has not started, the
        // first being processLogs' zero-length pass over the same log. By then the new workday
        // starts at the reset with this one before it, and countRestBreaks has moved this
        // workday's endOfLastBreak to the reset. Where either credit applies it refills the
        // shortfall and nothing drops.
        const shortfall = totalRest - restBeforeBreak;
        const outgoingWorkday = { start: state.workday.start, endOfLastBreak: resetDate };
        if (this.generousRestCreditApplies(state, state.workday.start, state.workdays[state.workdays.length - 2], breakStart, resetDate, shortfall)
            || this.generousRestCreditApplies(state, resetDate, outgoingWorkday, breakStart, resetDate, shortfall)) {
            return undefined;
        }
        const requirementMetTime = resetDate.getTime() + this.earlierDaysShare(state, breakStart) + shortfall;
        return {
            resetDate,
            requirementMetDate: requirementMetTime < nextDayBoundary.getTime() ? new Date(requirementMetTime) : undefined,
        };
    }

    /**
     * How much of a regrowing break allocation countDailyRestBreaks hands to earlier days before
     * today: it walks the days the break spans oldest first, and each earlier day still short of
     * its rest takes min(its overlap with the break, its shortfall + deferralTime). The shortfall
     * excludes what this break already supplied, since the post-reset allocation replaces it.
     */
    private earlierDaysShare(state: IState, breakStart: Date): number {
        const dayStart = state.day.date.getTime();
        let share = 0;
        for (const day of state.days) {
            const start = day.date.getTime();
            const end = getBeginningOfNextDay(day.date, state.toLocalTime, state.toUtcTime).getTime();
            const totalRest = getTotalDailyRestRequired(day, state.config);
            if (start >= dayStart || end <= breakStart.getTime() || !day.restInfo || totalRest === undefined) {
                continue;
            }
            const fromThisBreak = day.restInfo
                .filter(r => !r.isWt && r.fromDate.getTime() >= breakStart.getTime())
                .reduce((sum, r) => sum + (r.toDate.getTime() - r.fromDate.getTime()), 0);
            const shortfall = totalRest - ((day.restCount ?? 0) - fromThisBreak);
            if (shortfall > 0) {
                share += Math.min(end - Math.max(start, breakStart.getTime()), shortfall + (day.deferralTime ?? 0));
            }
        }
        return share;
    }

    /**
     * At the log boundary that opens an off-duty break in a workday that has not started, when
     * checkIfWorkdayReset's MYG-16078 credit lands: one millisecond in, consecutiveOffCount is no
     * longer 0, and checkGenerousDailyRestCanadaHos credits the whole remaining daily rest from
     * the break's start. Mirrors its guards as they read at that first tick, when the break
     * starts at nextDate.
     */
    private generousRestCreditDate(state: IState): Date | undefined {
        const remaining = getRemainingDailyRestRequired(state.day, state.config);
        const countsAsOff = state.current?.status !== "WT" || state.config.workday?.consecutiveOffCountIncludesWt !== false;
        if (state.workday.hasStarted || !state.workday.offDutyMinimum || !countsAsOff
            || !state.day.restInfo || !remaining || state.day.restCount === undefined) {
            return undefined;
        }
        const firstTick = new Date(state.nextDate.getTime() + 1);
        const previousWorkday = state.workdays[state.workdays.length - 2];
        return this.generousRestCreditApplies(state, state.workday.start, previousWorkday, state.nextDate, firstTick, remaining) ? firstTick : undefined;
    }

    /**
     * Whether checkGenerousDailyRestCanadaHos credits the remaining daily rest when it runs at
     * `tick` for an off-duty break that started at `breakStart`, while the workday that started
     * at `workdayStart` is current and `previousWorkday` is the one before it.
     */
    private generousRestCreditApplies(
        state: IState,
        workdayStart: Date | undefined,
        previousWorkday: Pick<IWorkday, "start" | "endOfLastBreak"> | undefined,
        breakStart: Date,
        tick: Date,
        remaining: number
    ): boolean {
        const dayStart = state.day.date.getTime();
        if (workdayStart === undefined || workdayStart.getTime() <= dayStart || !previousWorkday) {
            return false;
        }
        const isPreviousWorkdayFirstResetOfDay = previousWorkday.start && previousWorkday.start.getTime() <= dayStart;
        const previousResetOverlapsBreak = previousWorkday.endOfLastBreak && previousWorkday.endOfLastBreak.getTime() >= breakStart.getTime();
        if (isPreviousWorkdayFirstResetOfDay && previousResetOverlapsBreak) {
            return false;
        }
        if (getDateIndex(tick, state.toLocalTime) !== getDateIndex(breakStart, state.toLocalTime)) {
            return false;
        }
        return getPeriodOverlap(
            state.day.date,
            new Date(getBeginningOfNextDay(state.day.date, state.toLocalTime, state.toUtcTime).getTime() - 1000),
            breakStart,
            new Date(breakStart.getTime() + remaining)
        ) !== undefined;
    }

    /**
     * Under disableDstDailyAvailabilityFix the requirement-met formula finds day+2 by adding one
     * calendar day with setDate before taking the next local midnight, so on a day whose length is
     * not 24h it reaches day+2 before midnight: the instant one setDate day before day+2.
     */
    private legacyDstShiftDate(state: IState, nextDayBoundary: Date): Date | undefined {
        if (!state.customCodes.disableDstDailyAvailabilityFix) {
            return undefined;
        }
        const shift = getBeginningOfDay(state.nextDate, state.toLocalTime, state.toUtcTime, 2);
        shift.setDate(shift.getDate() - 1);
        return shift.getTime() > state.nextDate.getTime() && shift.getTime() < nextDayBoundary.getTime() ? shift : undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined || c.restNeeded === undefined) return undefined;

        const nextDayBoundary = this.nextDayBoundary(state);

        switch (c.stateKind) {
            case "RequirementMetOnDuty": {
                // Midnight re-zeroes the new day's requirement, so the look-ahead jumps a full
                // day forward.
                if (c.zeroedRequirementFollowsEvalDay) {
                    return {
                        nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" }
                    };
                }
                // Otherwise the look-ahead is continuous at midnight and its zero-crossing is an
                // exhaustion event, handled by exhaustion(). No rate-change inflection exists here.
                return {};
            }
            case "RequirementMetOffDuty": {
                // The look-ahead value falls at −1 until one of these moves it: a workday reset
                // before midnight that takes back the break's rest (a dip until the regrowing
                // allocation re-meets the requirement); midnight, when a credited break starts
                // filling the new day; or, for a break still short of restBreakMinimum at
                // midnight, its credit. Midnight alone moves nothing for an uncredited break.
                const creditEvent = this.creditEvent(state);
                const creditedByMidnight = creditEvent.getTime() <= nextDayBoundary.getTime();
                const resetDrop = this.workdayResetDrop(state, nextDayBoundary);
                const legacyDstShift = this.legacyDstShiftDate(state, nextDayBoundary);
                return {
                    nextDayBoundary: { factor: () => creditedByMidnight ? nextDayBoundary : undefined, label: "day boundary" },
                    creditEvent: { factor: () => creditedByMidnight ? undefined : creditEvent, label: "credit threshold" },
                    workdayResetDropsRest: { factor: () => resetDrop?.resetDate, label: "workday reset" },
                    requirementMet: { factor: () => resetDrop?.requirementMetDate, label: "requirement met" },
                    legacyDstShift: { factor: () => legacyDstShift, label: "day boundary (legacy DST)" },
                };
            }
            case "OnDuty": {
                return {
                    nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
                };
            }
            case "OffDutyUncreditedNoLog": {
                return {};
            }
            case "OffDutyUncreditedAtBoundary": {
                const generousRestCredit = this.generousRestCreditDate(state);
                if (generousRestCredit) {
                    return {
                        generousRestCredit: { factor: () => generousRestCredit, label: "workday reset rest credit" },
                    };
                }
                const creditEvent = this.creditEvent(state);
                return {
                    nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
                    creditEvent: { factor: () => creditEvent, label: "credit threshold" },
                };
            }
            case "OffDutyUncredited": {
                const creditEvent = this.creditEvent(state);
                return {
                    nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
                    creditEvent: { factor: () => creditEvent, label: "credit threshold" },
                };
            }
            case "OffDutyCredited": {
                const requirementMetEvent = new Date(state.nextDate.getTime() + c.restNeeded);
                return {
                    nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
                    requirementMet: { factor: () => requirementMetEvent, label: "requirement met" },
                };
            }
        }
    }
}

export const extractDailyRestConditions = (state: IState): DailyRestConditions =>
    new DailyRestCalculator().extractConditions(state) as DailyRestConditions;

export const computeDailyRestResult = (state: IState): TComputedAvailabilityResult | undefined =>
    new DailyRestCalculator().compute(state);
